import { useRef } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';

import { cn } from '@/shared/lib/cn';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';

import { DocThumb } from './DocThumb';

export interface PanelCategory {
  key: string;
  title: string;
  /** Счётчик рядом с категорией в дропдауне (необязательно). */
  count?: number;
}

export interface PanelDoc {
  id: string | number;
  name: string;
  /** Готовая строка даты под именем (необязательно). */
  date?: string;
}

interface DocumentsPanelProps {
  categories: PanelCategory[];
  category: string;
  onCategoryChange: (key: string) => void;
  catOpen: boolean;
  onCatOpenChange: (open: boolean) => void;

  items: PanelDoc[];
  search: string;
  onSearchChange: (value: string) => void;

  selectedId: string | number | null;
  onSelect: (item: PanelDoc) => void;
  /** Путь к мини-превью файла (картинки) через авторизованный прокси. Вернуть
   *  undefined для не-картинок — тогда рисуется обычная иконка. */
  getThumbUrl?: (item: PanelDoc) => string | undefined;

  /** Если задан — показать кнопку «Загрузить документ». undefined = read-only. */
  onUpload?: (file: File) => void;
  uploading?: boolean;

  /** Если задан — показывать корзину на hover элемента. */
  onDelete?: (item: PanelDoc) => void;
  deletingId?: string | number | null;

  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;

  emptyText?: string;
  /** Переопределение высоты/фона корня. */
  className?: string;
}

const FOLDER_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#49577A"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
  >
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

/** Презентационная панель документов: выбор категории (дропдаун со счётчиками),
 *  поиск, кнопка загрузки и вертикальный список файлов с пагинацией/удалением.
 *  Данные и обработчики — снаружи. Используется в отделе и в задаче. */
export function DocumentsPanel({
  categories,
  category,
  onCategoryChange,
  catOpen,
  onCatOpenChange,
  items,
  search,
  onSearchChange,
  selectedId,
  onSelect,
  getThumbUrl,
  onUpload,
  uploading = false,
  onDelete,
  deletingId = null,
  loading = false,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  emptyText = 'Нет документов',
  className,
}: DocumentsPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const catWrapRef = useRef<HTMLDivElement>(null);
  const activeTitle = categories.find((c) => c.key === category)?.title ?? '';

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && onUpload) {
      onUpload(file);
    }
  }

  return (
    <div
      className={cn(
        'flex h-[792px] flex-col gap-3.5 rounded-[16px] border border-[#EAECF3] bg-white p-[18px] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_10px_28px_rgba(16,24,40,0.04)]',
        className
      )}
    >
      {/* Выбор категории */}
      <div ref={catWrapRef} className="relative z-20 shrink-0">
        <button
          type="button"
          onClick={() => onCatOpenChange(!catOpen)}
          className="flex w-full items-center justify-between gap-3 rounded-[12px] border border-[#EAECF3] bg-[#F6F7FB] px-4 py-3.5"
        >
          <span className="flex min-w-0 items-center gap-2.5">
            {FOLDER_ICON}
            <span className="truncate text-[15px] font-semibold text-[#2b3658]">
              {activeTitle}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-[#9aa1b4] transition-transform',
              catOpen && 'rotate-180'
            )}
            strokeWidth={2.4}
          />
        </button>

        {catOpen && (
          <FloatingPanel
            anchorRef={catWrapRef}
            align="stretch"
            sideOffset={8}
            className="overflow-hidden rounded-[12px] border border-[#EAECF3] bg-white shadow-[0_14px_34px_rgba(16,24,40,0.2)]"
          >
            {categories.map((c) => {
              const active = c.key === category;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => {
                    onCategoryChange(c.key);
                    onCatOpenChange(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2.5 border-b border-[#F4F5FA] px-4 py-[13px] text-left last:border-b-0',
                    active ? 'bg-[#F6F7FB]' : 'bg-white'
                  )}
                >
                  <span
                    className={cn(
                      'text-[14px] text-[#2b3658]',
                      active ? 'font-bold' : 'font-medium'
                    )}
                  >
                    {c.title}
                  </span>
                  <span className="flex shrink-0 items-center gap-2.5">
                    {c.count !== undefined && (
                      <span className="text-[13px] text-[#aab0c0]">
                        {c.count}
                      </span>
                    )}
                    {active && (
                      <Check
                        className="h-4 w-4 text-[#57B792]"
                        strokeWidth={2.6}
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </FloatingPanel>
        )}
      </div>

      {/* Поиск */}
      <div className="flex shrink-0 items-center gap-2.5 rounded-[12px] border border-[#EAECF3] bg-[#F6F7FB] px-4 py-[13px]">
        <Search
          className="h-[17px] w-[17px] shrink-0 text-[#9aa1b4]"
          strokeWidth={2}
        />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Поиск документа"
          className="w-full bg-transparent text-[14px] text-[#2b3658] outline-none placeholder:text-[#aab0c0]"
        />
      </div>

      {/* Список документов */}
      <div className="-mx-1 flex flex-1 flex-col gap-2 overflow-y-auto px-1 [scrollbar-color:#D7DBE6_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#D7DBE6] [&::-webkit-scrollbar]:w-1">
        {/* Загрузка */}
        {onUpload && (
          <>
            <input ref={fileRef} type="file" hidden onChange={onPick} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex w-full shrink-0 items-center gap-3 rounded-[12px] border border-dashed border-[#C9CFDD] bg-white px-3.5 py-3 text-left text-[#9aa1b4] disabled:opacity-60"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] bg-[#F6F7FB] text-[#9aa1b4]">
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </span>
              <span className="text-[14px] font-medium">
                Загрузить документ
              </span>
            </button>
          </>
        )}

        {loading ? (
          <div className="grid flex-1 place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-[13px] text-[#aab0c0]">
            {search.trim() ? 'Ничего не найдено' : emptyText}
          </div>
        ) : (
          <>
            {items.map((d) => {
              const isSel = selectedId === d.id;
              const deleting = deletingId === d.id;
              return (
                <div
                  key={d.id}
                  className={cn(
                    'group relative flex w-full shrink-0 items-center gap-[13px] rounded-[12px] border px-3.5 py-[13px]',
                    isSel
                      ? 'border-[#CBD5EA] bg-[#F6F8FC]'
                      : 'border-[#EEF0F6] bg-white'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(d)}
                    className="flex min-w-0 flex-1 items-center gap-[13px] text-left"
                  >
                    <DocThumb
                      thumbUrl={getThumbUrl?.(d)}
                      className="h-[29px] w-[24px] shrink-0"
                      accent={isSel}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-[#2b3658]">
                        {d.name}
                      </span>
                      {d.date && (
                        <span className="mt-[3px] block text-[12px] text-[#9aa1b4]">
                          {d.date}
                        </span>
                      )}
                    </span>
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 shrink-0',
                        isSel ? 'text-[#34B58A]' : 'text-[#c0c6d4]'
                      )}
                      strokeWidth={2.4}
                    />
                  </button>

                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete(d)}
                      disabled={deleting}
                      aria-label="Удалить документ"
                      className="absolute right-2 top-2 hidden h-5 w-5 items-center justify-center rounded-full bg-white text-[#E5484D] shadow-[0px_2px_6px_rgba(0,0,0,0.15)] group-hover:flex"
                    >
                      {deleting ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </button>
                  )}
                </div>
              );
            })}

            {hasMore && onLoadMore && (
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loadingMore}
                className="flex w-full shrink-0 items-center justify-center rounded-[12px] border border-dashed border-[#C9CFDD] py-3 text-[13px] text-[#9aa1b4] hover:bg-[#F6F7FB]"
              >
                {loadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Показать ещё'
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
